import _ from "lodash";
import { DragAndDropList, Modal, SearchInput } from "@toddle-edu/ds-web";
import React, { useEffect, useRef, useState } from "react";
import { useI18n, useMount } from "Hooks";
import classes from "./TagsSetupModal.scss";
import update from "immutability-helper";

import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import FullScreenLoader from "UIComponents/FullScreenLoader";

import {
  reorderMultiLevelTags,
  updateMultiLevelTagLabel,
  deleteMultiLevelTag,
  createMultiLevelTags,
} from "NodeEditor/Modules/NodeEditorModule";
import { onBulkRemoveTags } from "NodeEditor/Utils/Utils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import DeleteTagModal from "NodeEditor/Components/Table/Components/TableRow/Components/DeleteTagModal";

const TagsSetupModal = props => {
  const {
    onClose,
    organizationTags,
    inputForMultiLevelTagsQuery,
    reorderMultiLevelTags,
    updateMultiLevelTagLabel,
    deleteMultiLevelTag,
    createMultiLevelTags,
    nodes,
    setNodes,
    updateNodesInDraft,
    setToastMsg,
  } = props;
  const { t } = useI18n();
  const [tagList, setTagList] = useState(organizationTags);
  const [deleteTagId, setDeleteTagId] = useState();
  const [isLoading, setIsLoading] = useState(false);
  // we are using useRef instead of useState because the onBlur event don't have the updated value of the input in case of useState
  const updatedIdLabel = useRef();
  // maintaining the search in state to show real time search results
  // and using ref to get the updated value of the input in the blur function
  const searTextRef = useRef("");
  const createTagInputOpen = useRef();
  const tagListRef = useRef([]);

  const [searchText, setSearchText] = useState("");
  const closeDeleteModal = () => {
    setDeleteTagId(null);
  };
  useEffect(() => {
    const filteredArray = _.filter(organizationTags, item => {
      return item.value.toLowerCase().includes(searchText.toLowerCase());
    });

    if (createTagInputOpen && createTagInputOpen.current)
      filteredArray.push(_.last(tagList));
    tagListRef.current = organizationTags;
    setTagList(filteredArray);
  }, [organizationTags, searchText]);

  const onChangeSearchText = value => {
    searTextRef.current = value;
    setSearchText(value);
  };

  useMount(() => {
    return () => {
      onChangeSearchText("");
    };
  });

  const getTagLabelFromIdMemoized = _.memoize(id => {
    return _.get(_.find(tagList, { id }), "value", "");
  });

  const onConfirmDelete = async () => {
    setIsLoading(true);
    const removedItemList = [{ id: deleteTagId }];

    // removing tags from each node.
    const selectedNodeIds = _.map(nodes, "id");
    const updatedNodes = onBulkRemoveTags({
      nodes,
      setNodes,
      selectedNodeIds,
      itemsList: removedItemList,
    });
    updateNodesInDraft({ updatedNodes });
    await deleteMultiLevelTag({
      input: {
        ids: [deleteTagId],
      },
      inputForMultiLevelTagsQuery,
    });

    setIsLoading(false);

    setDeleteTagId(null);
  };

  // This function will return the last action applied on the tag list.
  const getLastActionApplied = id => {
    // if we add new tag then it will have random id which will be inserted at the last position of the list.
    const item = _.find(tagListRef.current, { id });
    return item ? "update" : "create";
  };

  // This function will check if the name already exist in the old tag list.
  // If the user writes same label in the input field then we will not allow to update the label.
  // we will return 1 if the name already exist in the old tag list.
  // we will return 0 if the name does not exist in the old tag list.
  // we will return -1 if the previous label is same as current label for given id.
  const checkNameAlreadyExist = () => {
    const { id, label: _label } = updatedIdLabel.current;
    const label = _label.trim();
    let isNameAlreadyExist = 0;
    _.forEach(tagListRef.current, (tag, index) => {
      if (tag.value === label) {
        if (tag.id === id) {
          isNameAlreadyExist = -1;
        } else isNameAlreadyExist = 1;
        return false;
      }
    });
    return isNameAlreadyExist;
  };

  const onBlur = async e => {
    // We will not do any action if user has not changed the label.
    if (!updatedIdLabel.current) return;
    const { label: _label, id } = updatedIdLabel.current;
    const label = _label.trim();
    const lastAppliedAction = getLastActionApplied(id);
    const isLabelNameEmpty = _.isEmpty(label);
    const isLabelNameAlreadyExist = checkNameAlreadyExist();
    if (isLabelNameEmpty || isLabelNameAlreadyExist) {
      if (lastAppliedAction === "create") {
        setTagList(prevList => {
          // Create a new array with all elements from the previous state
          const newArray = [...prevList];
          const lastTag = newArray[newArray.length - 1];
          // Change the last element's label of the new array
          newArray[newArray.length - 1] = update(lastTag, {
            value: {
              $set: "",
            },
          });
          setTagList(newArray);
        });
      }
      //update
      else {
        // find from old tag list having updated id and update the tagList with the old tag label.
        const oldTag = _.find(tagListRef.current, { id });
        const updatedTagList = [];
        _.forEach(tagListRef.current, (tag, index) => {
          if (tag.id === id) {
            updatedTagList.push(
              update(tag, {
                value: {
                  $set: oldTag.value,
                },
              })
            );
          } else if (
            tag.value.toLowerCase().includes(searTextRef.current.toLowerCase())
          ) {
            updatedTagList.push(tag);
          }
        });
        // If there is wrong update in any label then we will also close the empty input field.
        createTagInputOpen.current = false;
        setTagList(updatedTagList);
      }

      // fire toast msgs
      if (isLabelNameEmpty) {
        setToastMsg({
          msg: "scopeAndSequence:tag_name_cannot_be_empty",
          type: "warning",
        });
      } else if (isLabelNameAlreadyExist === 1) {
        setToastMsg({
          msg: "scopeAndSequence:tag_name_already_exist",
          type: "warning",
        });
      }
    } else {
      if (lastAppliedAction === "create") {
        createTagInputOpen.current = false;
        await createMultiLevelTags({
          input: [{ label }],
          inputForMultiLevelTagsQuery,
        });
      } else {
        await updateMultiLevelTagLabel({
          input: {
            id,
            label,
          },
        });
      }
    }
    updatedIdLabel.current = null;
  };

  const onValueChange = async (updatedTagList, { action, id }) => {
    switch (action) {
      case "create": {
        createTagInputOpen.current = true;
        setTagList(updatedTagList);
        break;
      }

      case "update": {
        const updatedTagItem = _.find(updatedTagList, { id: id });
        updatedIdLabel.current = { id, label: updatedTagItem.value };
        setTagList(updatedTagList);
        break;
      }

      case "delete": {
        // remove unsaved item
        if (
          _.get(_.last(tagList), "id") == id &&
          !_.find(organizationTags, { id })
        ) {
          updatedIdLabel.current = null;
          createTagInputOpen.current = false;
          setTagList(_.initial(tagList));
        } else {
          // Handle if the tag is new and not saved in the database (i.e. still in the cache).
          const lastTag = _.last(tagList);
          if (_.startsWith(_.get(lastTag, "id", ""), "NEW_")) return;
          setDeleteTagId(id);
        }
        break;
      }

      case "reorder": {
        let draggedItem = null;

        _.forEach(tagList, (tag, index) => {
          if (tag.id !== updatedTagList[index].id) {
            if (updatedTagList[index + 1].id === tag.id) {
              draggedItem = updatedTagList[index];
            } else {
              draggedItem = tag;
            }
            return false;
          }
        });

        let draggedId, putAfterItemId;

        if (draggedItem) {
          draggedId = draggedItem.id;

          const draggedIndex = _.findIndex(updatedTagList, { id: draggedId });

          if (draggedIndex > 0) {
            putAfterItemId = updatedTagList[draggedIndex - 1].id;
          } else {
            putAfterItemId = null;
          }
          setTagList(updatedTagList);
          await reorderMultiLevelTags({
            id: draggedId,
            putAfterItemId,
            inputForMultiLevelTagsQuery,
          });
        }
      }
    }
  };

  return (
    <Modal width="480px" height="600px" onClose={onClose}>
      <Modal.Header
        isClosable
        title={t("scopeAndSequence:setup_tags")}
      ></Modal.Header>
      <Modal.Body isScrollable>
        <SearchInput
          placeholder={t("common:search_for_a_with_label", {
            label: t("common:tag"),
          })}
          value={searchText}
          onChange={onChangeSearchText}
        />
        <div className={classes.dndContainer}>
          <DragAndDropList
            value={tagList}
            onValueChange={onValueChange}
            showAddAnotherItemButton={true}
            onBlur={onBlur}
            canDeleteAllItems={true}
            disableDragForEmptyItem={true}
          />
        </div>
        {deleteTagId ? (
          <DeleteTagModal
            title={getTagLabelFromIdMemoized(deleteTagId)}
            onClose={closeDeleteModal}
            onDeleteTag={onConfirmDelete}
          />
        ) : null}
        {isLoading ? <FullScreenLoader /> : null}
      </Modal.Body>
    </Modal>
  );
};

const mapActionCreators = {
  reorderMultiLevelTags,
  updateMultiLevelTagLabel,
  deleteMultiLevelTag,
  createMultiLevelTags,
  setToastMsg,
};

export default compose(connect(null, mapActionCreators))(TagsSetupModal);
